/**
 * "Show me on screen" links.
 *
 * The show_me_how tool answers a how-to question with `[Show me on screen](guide:<id>)`.
 * Clicking that button asks the window hosting this chat (the Addy app) to start a
 * guided cursor for walkthrough <id>. When no host answers (Help Center, Gmail, a
 * standalone tab), the app is opened in a new tab with the walkthrough in the URL.
 */
import GlobalVariables from "../models/globalVariables";

export const GUIDE_LINK_SCHEME = "guide:";
export const NAVIGATE_LINK_SCHEME = "navigate:";
export const GUIDE_START_MESSAGE_KEY = "agent-guide-start";
export const GUIDE_ACK_MESSAGE_KEY = "agent-guide-ack";
export const GUIDE_PROGRESS_MESSAGE_KEY = "agent-guide-progress";
export const GUIDE_DEEP_LINK_PARAM = "addyGuide";
export const NAVIGATE_MESSAGE_KEY = "agent-navigate";
export const NAVIGATE_ACK_MESSAGE_KEY = "agent-navigate-ack";
export const DEFAULT_APP_ORIGIN = "https://app.addy.com";
const DEFAULT_ACK_TIMEOUT_MS = 900;
const WALKTHROUGH_ID_PATTERN = /^[A-Za-z0-9_-]{6,128}$/;

/**
 * DOMPurify's default URI allow-list drops hrefs with custom schemes, which is
 * exactly what the agent's action links use. This keeps `navigate:` and `guide:`
 * (and nothing else beyond the defaults, so `javascript:` stays blocked).
 */
export const AGENT_LINK_URI_REGEXP =
  /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|sms|cid|xmpp|matrix|navigate|guide):|[^a-z]|[a-z+.-]+(?:[^a-z+.:-]|$))/i;

export function isGuideHref(href) {
  return typeof href === "string" && href.startsWith(GUIDE_LINK_SCHEME);
}

/**
 * @param {string} href - e.g. "guide:AbC123xyz"
 * @returns {string|null} the walkthrough id, or null when the link is malformed
 */
export function parseGuideHref(href) {
  if (!isGuideHref(href)) return null;
  const walkthroughId = href.slice(GUIDE_LINK_SCHEME.length).trim();
  return WALKTHROUGH_ID_PATTERN.test(walkthroughId) ? walkthroughId : null;
}

/**
 * The Addy app origin to deep-link into. Prefers the origin of the page hosting
 * this chat when it is one of the known Addy app hosts (keeps dev on dev).
 * @param {string|null|undefined} currentURL - the `currentURL` iframe parameter
 */
export function resolveAppOrigin(currentURL) {
  const knownOrigins = (GlobalVariables.getClientDomain?.() || [])
    .map((domain) => {
      try {
        return new URL(domain).origin;
      } catch (_) {
        return null;
      }
    })
    .filter(Boolean);
  if (currentURL) {
    try {
      const origin = new URL(currentURL).origin;
      if (knownOrigins.includes(origin)) return origin;
    } catch (_) {
      // Not a URL; fall through to the default app origin.
    }
  }
  return DEFAULT_APP_ORIGIN;
}

export function buildGuideDeepLink(walkthroughId, currentURL) {
  const origin = resolveAppOrigin(currentURL);
  return `${origin}/loans?${GUIDE_DEEP_LINK_PARAM}=${encodeURIComponent(walkthroughId)}`;
}

function createRequestId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `guide-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/**
 * Post `message` to the hosting window and wait for `ackKey` with the same requestId.
 * Resolves true when the host acknowledged within the timeout.
 */
function requestFromHost({ key, ackKey, value, parentWindow, eventTarget, timeoutMs }) {
  if (!parentWindow || !eventTarget) return Promise.resolve(false);
  const requestId = createRequestId();
  return new Promise((resolve) => {
    let settled = false;
    const finish = (acked) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      eventTarget.removeEventListener("message", onMessage);
      resolve(acked);
    };
    const onMessage = (event) => {
      const data = event?.data;
      if (data?.key === ackKey && data?.value?.requestId === requestId) {
        finish(true);
      }
    };
    const timer = setTimeout(() => finish(false), timeoutMs);
    eventTarget.addEventListener("message", onMessage);
    try {
      parentWindow.postMessage({ key, value: { ...value, requestId } }, "*");
    } catch (_) {
      finish(false);
    }
  });
}

/**
 * Ask the hosting window to start the walkthrough and wait for its acknowledgement.
 * Resolves true when the host acknowledged within the timeout.
 */
export function requestGuidedWalkthroughFromHost({
  walkthroughId,
  goal,
  articleUrl,
  parentWindow,
  eventTarget = typeof window !== "undefined" ? window : null,
  timeoutMs = DEFAULT_ACK_TIMEOUT_MS,
}) {
  return requestFromHost({
    key: GUIDE_START_MESSAGE_KEY,
    ackKey: GUIDE_ACK_MESSAGE_KEY,
    value: {
      walkthroughId,
      source: "sidebar",
      // The question and the article it was answered from let the app
      // start a known route at once instead of waiting for the planner.
      ...(goal ? { goal: String(goal).slice(0, 500) } : {}),
      ...(articleUrl ? { articleUrl } : {}),
    },
    parentWindow,
    eventTarget,
    timeoutMs,
  });
}

/**
 * Start a walkthrough from a clicked guide link: in the Addy app the host takes
 * over; anywhere else the app opens in a new tab with the walkthrough queued.
 * @returns {Promise<{ started: boolean, via: "host" | "deep-link" }>}
 */
export async function startGuidedWalkthroughFromLink({
  walkthroughId,
  goal,
  articleUrl,
  parentWindow,
  currentURL,
  openWindow = (url) => window.open(url, "_blank", "noopener"),
  timeoutMs,
}) {
  const acked = await requestGuidedWalkthroughFromHost({
    walkthroughId,
    goal,
    articleUrl,
    parentWindow,
    timeoutMs,
  });
  if (acked) return { started: true, via: "host" };
  openWindow(buildGuideDeepLink(walkthroughId, currentURL));
  return { started: true, via: "deep-link" };
}

/**
 * @param {string} href - e.g. "navigate:/scenarios/abc"
 * @returns {string|null} the app path, or null when it is not a same-app path
 */
export function parseNavigateHref(href) {
  if (typeof href !== "string" || !href.startsWith(NAVIGATE_LINK_SCHEME)) return null;
  const path = href.slice(NAVIGATE_LINK_SCHEME.length).trim();
  return path.startsWith("/") && !path.startsWith("//") ? path : null;
}

/**
 * The Addy app's own origins (the borrower portals in getClientDomain are a different app).
 */
const appOrigins = () =>
  (GlobalVariables.getClientDomain?.() || [])
    .filter((domain) => !/portal/i.test(domain))
    .map((domain) => {
      try {
        return new URL(domain).origin;
      } catch (_) {
        return null;
      }
    })
    .filter(Boolean);

/**
 * The app path of a full Addy app URL the agent wrote out, such as a tool's
 * "https://app.addy.so/loans/abc?tabValue=processingChecklist", so it opens in the app like a
 * navigate: link instead of loading the app inside the chat.
 * @param {string} href
 * @returns {string|null} path with query and hash, or null when it is not an Addy app URL
 */
export function appPathFromUrl(href) {
  if (typeof href !== "string" || !/^https?:\/\//i.test(href)) return null;
  let url;
  try {
    url = new URL(href);
  } catch (_) {
    return null;
  }
  if (!appOrigins().includes(url.origin)) return null;
  const path = `${url.pathname}${url.search}${url.hash}`;
  return path.startsWith("/") && !path.startsWith("//") ? path : null;
}

/**
 * Open an app page from a clicked navigate link: in the Addy app's sidebar the host
 * navigates; anywhere else (Guideline Search, the extension, Gmail, a standalone tab)
 * the page opens in a new tab.
 * @returns {Promise<{ via: "host" | "deep-link" }>}
 */
export async function navigateFromLink({
  path,
  parentWindow,
  currentURL,
  openWindow = (url) => window.open(url, "_blank", "noopener"),
  eventTarget = typeof window !== "undefined" ? window : null,
  timeoutMs = DEFAULT_ACK_TIMEOUT_MS,
}) {
  const acked = await requestFromHost({
    key: NAVIGATE_MESSAGE_KEY,
    ackKey: NAVIGATE_ACK_MESSAGE_KEY,
    value: { path },
    parentWindow,
    eventTarget,
    timeoutMs,
  });
  if (acked) return { via: "host" };
  openWindow(`${resolveAppOrigin(currentURL)}${path}`);
  return { via: "deep-link" };
}

/**
 * Turn a progress event from the host into the chat status line shown under the
 * answer, or null when nothing should be shown.
 */
export function formatGuideProgressMessage(progress) {
  if (!progress || typeof progress !== "object") return null;
  const stepLabel = progress.stepIndex ? `Step ${progress.stepIndex}` : "Step";
  // Same voice as the on-screen card: short, calm, no emoji.
  switch (progress.status) {
    case "started":
      return `**Guiding you on screen**${progress.goal ? `: ${progress.goal}` : ""}. Follow the highlighted cursor in the app. Press Esc to stop.`;
    case "step":
      return `**${stepLabel}**: ${progress.caption || ""}`.trim();
    case "done":
      return `**All done**${progress.summary ? `: ${progress.summary}` : "."}`;
    case "stopped":
      return "Guide stopped. Ask again whenever you want to pick it back up.";
    case "failed":
      // The app's message is already a full sentence ("I lost track of..."), so it is not prefixed again.
      return progress.message ? `**Guide stopped early.** ${progress.message}` : "**Guide stopped early.** Ask again to start over.";
    default:
      return null;
  }
}

const HELP_ARTICLE_URL_PATTERN = /^https:\/\/help\.addy\.so\/docs\/[\w\-/]{1,300}$/;

/**
 * The first Help Center article linked in a rendered answer, or "".
 * @param {ParentNode | null | undefined} container
 */
export function findHelpArticleUrl(container) {
  if (!container || typeof container.querySelectorAll !== "function") return "";
  for (const anchor of Array.from(container.querySelectorAll("a[href]"))) {
    const href = (anchor.getAttribute("href") || "").split("#")[0].split("?")[0];
    if (HELP_ARTICLE_URL_PATTERN.test(href)) return href;
  }
  return "";
}

export const GUIDE_PREFETCH_MESSAGE_KEY = "agent-guide-prefetch";
const GUIDE_MARKDOWN_LINK_PATTERN = /\(guide:([A-Za-z0-9_-]{6,128})\)/;
const HELP_ARTICLE_IN_TEXT_PATTERN = /https:\/\/help\.addy\.so\/docs\/[\w\-/]{1,300}/;
const prefetchedWalkthroughIds = new Set();

/**
 * Tell the hosting app that a finished answer carries a guide button, so it can
 * plan the first step while the user reads. Sent once per walkthrough; the app
 * ignores it for questions it has a known route for.
 */
export function prefetchGuidedWalkthrough({ messageText, goal, parentWindow }) {
  if (!parentWindow || typeof messageText !== "string") return false;
  const match = messageText.match(GUIDE_MARKDOWN_LINK_PATTERN);
  if (!match || prefetchedWalkthroughIds.has(match[1])) return false;
  prefetchedWalkthroughIds.add(match[1]);
  const article = messageText.match(HELP_ARTICLE_IN_TEXT_PATTERN);
  try {
    parentWindow.postMessage(
      {
        key: GUIDE_PREFETCH_MESSAGE_KEY,
        value: {
          walkthroughId: match[1],
          source: "sidebar",
          ...(goal ? { goal: String(goal).slice(0, 500) } : {}),
          ...(article ? { articleUrl: article[0].replace(/[.,;:]+$/, "") } : {}),
        },
      },
      "*"
    );
    return true;
  } catch (_) {
    return false;
  }
}

export const GUIDE_BUTTON_CLASS = "agent-guide-link";
export const GUIDE_TOP_CLASS = "agent-guide-top";
// Marks nodes that copying the answer leaves out (see copyMessageContent).
export const COPY_SKIP_ATTRIBUTE = "data-copy-skip";

/**
 * Lays out a rendered answer's "Show me on screen" buttons. The button stays
 * where the answer put it (usually the end) while streaming and, once the
 * answer has finished, moves above the written steps so the guide can start
 * without scrolling. The buttons are kept out of copied text. Where the guide
 * is not available (see isGuideAvailable) the buttons are removed instead.
 * @param {HTMLElement} container - the rendered answer
 * @param {{ moveToTop?: boolean, available?: boolean }} [options]
 */
export function placeGuideButtons(container, { moveToTop = true, available = true } = {}) {
  if (!container || typeof container.querySelectorAll !== "function") return;
  const buttons = Array.from(container.querySelectorAll(`a.${GUIDE_BUTTON_CLASS}`));
  if (!buttons.length) return;

  buttons.forEach((button) => {
    const block = button.parentElement;
    const isAlone =
      block && block !== container && block.textContent.trim() === button.textContent.trim();
    const node = isAlone ? block : button;
    if (available) {
      node.setAttribute(COPY_SKIP_ATTRIBUTE, "");
    } else {
      node.remove();
    }
  });
  if (!available) return;

  const firstBlock = container.firstElementChild;
  const alreadyOnTop =
    firstBlock &&
    (firstBlock.classList.contains(GUIDE_BUTTON_CLASS) ||
      firstBlock.querySelector(`a.${GUIDE_BUTTON_CLASS}`));
  if (!moveToTop || alreadyOnTop) return;

  const [firstButton] = buttons;
  const originalBlock = firstButton.parentElement;
  const topBlock = container.ownerDocument.createElement("p");
  topBlock.className = GUIDE_TOP_CLASS;
  topBlock.setAttribute(COPY_SKIP_ATTRIBUTE, "");
  firstButton.removeAttribute(COPY_SKIP_ATTRIBUTE);
  topBlock.appendChild(firstButton);
  container.insertBefore(topBlock, container.firstChild);
  if (originalBlock && originalBlock !== container && !originalBlock.textContent.trim()) {
    originalBlock.remove();
  }
}

/*
 * Where "Show me on screen" is offered. The guide does not run at mobile size
 * or in the Addy mobile app, so the button is hidden there. Inside the Addy app
 * the app decides (?guideAvailable=1|0, then "guideAvailability" messages as
 * the window is resized). Elsewhere (Help Center, Gmail) the button opens the
 * app in a new tab, so it is hidden on phones, tablets and the mobile app.
 */
export const GUIDE_AVAILABLE_PARAM = "guideAvailable";
export const GUIDE_AVAILABILITY_MESSAGE_KEY = "guideAvailability";
const MOBILE_USER_AGENT_PATTERN = /AddyAI-Mobile|Android|iPhone|iPad|iPod|Mobile/i;

export function isMobileDevice(win = typeof window !== "undefined" ? window : null) {
  if (!win) return false;
  const userAgent = win.navigator?.userAgent || "";
  return MOBILE_USER_AGENT_PATTERN.test(userAgent) || Boolean(win.Capacitor?.isNativePlatform?.());
}

function readHostGuideAvailability() {
  try {
    const value = new URLSearchParams(window.location.search).get(GUIDE_AVAILABLE_PARAM);
    return value === "1" ? true : value === "0" ? false : null;
  } catch (_) {
    return null;
  }
}

let hostGuideAvailability;
const guideAvailabilityListeners = new Set();

function currentHostGuideAvailability() {
  if (hostGuideAvailability === undefined) hostGuideAvailability = readHostGuideAvailability();
  return hostGuideAvailability;
}

function handleGuideAvailabilityMessage(event) {
  if (event.data?.key !== GUIDE_AVAILABILITY_MESSAGE_KEY) return;
  const value = event.data.value;
  if (typeof value !== "boolean" || value === currentHostGuideAvailability()) return;
  hostGuideAvailability = value;
  guideAvailabilityListeners.forEach((listener) => listener());
}

export function isGuideAvailable() {
  const hostValue = currentHostGuideAvailability();
  return hostValue === null ? !isMobileDevice() : hostValue;
}

/** For useSyncExternalStore: listens to the app while anything is subscribed. */
export function subscribeGuideAvailability(listener) {
  if (!guideAvailabilityListeners.size && typeof window !== "undefined") {
    window.addEventListener("message", handleGuideAvailabilityMessage);
  }
  guideAvailabilityListeners.add(listener);
  return () => {
    guideAvailabilityListeners.delete(listener);
    if (!guideAvailabilityListeners.size && typeof window !== "undefined") {
      window.removeEventListener("message", handleGuideAvailabilityMessage);
    }
  };
}
